import * as yup from "yup";
import { useMemo } from "react";
import { Button, TextField, Grid, Container, Link } from "@mui/material";
import { Box } from "@mui/system";
import { mdiInformationOutline, mdiLock } from "@mdi/js";
import Icon from "@mdi/react";

import AppBar from "components/AppBar";
import AuthForm from "components/AuthForm";
import GdprConsent from "components/GdprConsent";
import HorizontalTextDivider from "components/HorizontalTextDivider";
import PageLayout from "components/PageLayout";
import AuthApi from "api/auth";
import useNotifier from "hooks/useNotifier";
import googleIcon from "assets/icons/google.png";
import config from "config";
import styles from "./signin.module.scss";

const validationSchema = yup.object().shape({
  email: yup.string().trim().required("Email address is required").email("Email address is not valid"),
  password: yup.string().required("Password is required"),
});

function SignIn() {
  const { showNotification } = useNotifier();
  const authApi = useMemo(() => new AuthApi(showNotification), [showNotification]);

  const handleSignIn = async (data: { email: string; password: string }) => {
    await authApi.signIn(data.email, data.password);
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    await authApi.signInWithGoogle();
  };

  return (
    <PageLayout
      appBar={
        <AppBar
          actions={[
            <Link
              alignItems='center'
              display='flex'
              gap={0.6}
              href={config.links.website}
              rel='noopener noreferrer'
              target='_blank'
            >
              <Icon path={mdiInformationOutline} size={0.8} /> About us
            </Link>,
          ]}
          backAction={false}
          noRadius
          showLogo
          title='GroupUp'
        />
      }
    >
      <AuthForm<SignInForm>
        avatarIcon={mdiLock}
        links={[
          { label: "Forgot password ?", href: "/forgot-password" },
          { label: "Sign Up", href: "/sign-up" },
        ]}
        submitLabel='Sign In'
        title='Sign In'
        validationSchema={validationSchema}
        onSubmit={handleSignIn}
      >
        <TextField name='email' label='Email address' autoComplete='email' fullWidth />
        <TextField name='password' label='Password' type='password' autoComplete='current-password' fullWidth />
      </AuthForm>

      <Container maxWidth='xs'>
        <Box mb={8}>
          <Grid container justifyContent='center' spacing={3}>
            <Grid item xs={12}>
              <HorizontalTextDivider text='OR' />
            </Grid>
            <Grid item xs={12}>
              <Button
                size='large'
                variant='lowercaseSimple'
                onClick={handleGoogleSignIn}
                startIcon={<img alt='google icon' src={googleIcon} className={styles.googleIcon} />}
                fullWidth
                classes={{
                  root: styles.btnText,
                  startIcon: styles.btnStartIcon,
                }}
              >
                Sign in with Google
              </Button>
            </Grid>
            <Grid item xs={12}>
              <GdprConsent withCheck={false} />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </PageLayout>
  );
}

export default SignIn;

interface SignInForm extends Record<string, unknown> {
  email: string;
  password: string;
}
